// 后厨汇总 grouping — turns the flat per-product rows the API returns into the
// per-store blocks the kitchen's handwritten 每日甜品汇总 sheet uses.
//
// The catalog is a messy 4-D cube (see migration 0015): `category` sometimes
// bakes the size into its label (经典6寸) while `size` ALSO carries it, and in
// prod the size string is written several ways (6" / 6'' / 6寸). We derive a
// `family` by stripping the size token off the category, and normalize every
// size notation to one canonical label so 6寸 never splits into duplicate cols.
//
//   rows (per product, one size)            families (per flavor, per store)
//   ┌───────────────────────┐   group by    ┌──────────────────────────────────┐
//   │ 经典6寸·奥利奥 size 6" │─ (family,     │ 经典 (storeBlock)                 │
//   │ 经典8寸·奥利奥 size 8" │   flavor) ──▶ │  奥利奥  NY{订6,订8,现,总} DT.. RH│
//   │ cks·经典组合  variant∅ │   then split  │          Total{订6,订8,总现,总数} │
//   │ 5寸·抹茶      size 5" │   per store    │ 5寸 (flavorCols) 蛋挞 (flat)      │
//   └───────────────────────┘               │ cks (total + store split)         │
//                                            └──────────────────────────────────┘
//
// Four render KINDS (see kindOf): storeBlock (经典/太阳花蛋挞/季节限定 — per-store
// 订6/订8/现/总), flavorCols (5寸 — stores as rows, flavors as columns),
// flat (蛋挞 — flavor + total only), cks (total + parenthesized store split).

const FAMILY_ORDER = ['经典', '5寸', '太阳花蛋挞', '季节限定', '蛋挞', '千层', '椰子冻', 'cks'];
const SIZE_ORDER = ['5寸', '6寸', '8寸', '10寸', '12寸', '其他'];

const FLAT_FAMILIES = new Set(['蛋挞']);
const FLAVORCOL_FAMILIES = new Set(['5寸']);
const CKS_FAMILIES = new Set(['cks']);

/** Render kind for a family — decides which sub-layout the sheet uses. */
export function kindOf(family) {
  if (CKS_FAMILIES.has(family)) return 'cks';
  if (FLAT_FAMILIES.has(family)) return 'flat';
  if (FLAVORCOL_FAMILIES.has(family)) return 'flavorCols';
  return 'storeBlock';
}

/** Category → display family: strip a trailing size token (`…6寸`) and a trailing
 *  `系列`, so 经典6寸/经典8寸 collapse to 经典 and 太阳花蛋挞系列 → 太阳花蛋挞.
 *  Falls back to the original category when stripping would empty it (e.g. 5寸). */
export function familyOf(category) {
  if (!category) return '其他';
  const stripped = category.replace(/系列$/, '').replace(/\s*\d+寸$/, '').trim();
  return stripped || category;
}

/** Normalize any size notation to one canonical label. Prod stores the same size
 *  several ways — 6", 6'' (two apostrophes), 6″, 6寸, or a bare 6 — and an un-
 *  normalized union would open a duplicate 订单 column per spelling. All inch/寸
 *  forms collapse to `N寸`; non-numeric labels (其他) pass through; null → null. */
export function sizeLabel(size) {
  if (size == null) return null;
  const s = String(size).trim();
  if (s === '') return null;
  const m = /^(\d+)\s*["'″”“寸]*$/.exec(s);   // digits + any run of quote-ish / 寸
  return m ? `${m[1]}寸` : s;
}

/** Display name: prefer the 口味 (variant); else strip the "family · " prefix off
 *  the product name so "cks · 经典组合" reads as "经典组合". */
function displayName(row) {
  if (row.variant) return row.variant;
  const name = row.name || '';
  return name.includes('·') ? name.split('·').pop().trim() : name;
}

function familyRank(f) { const i = FAMILY_ORDER.indexOf(f); return i < 0 ? 99 : i; }
function sizeRank(s) { const i = SIZE_ORDER.indexOf(s); return i < 0 ? 99 : i; }

/**
 * Group the API's flat per-product rows into families → flavors, with per-store
 * order-by-size + on-hand carried through so every sub-layout can render.
 *
 * @param {Array} rows   kitchen-sheet rows: { productId, name, category, variant,
 *                       size, perStore:{store:{ordered,onHand}}, total:{ordered,onHand} }
 * @param {string[]} stores  e.g. ['NY','DT','RH']
 * @returns {Array} families: [{ family, kind, sizes:string[], flavors:[{
 *   key, name,
 *   perStore:{ store:{ bySize:{size:qty}, nullOrders, onHand, total } },
 *   totBySize:{size:qty}, totNullOrders, totOrdered, totOnHand, totAll }] }]
 *   sorted by FAMILY_ORDER then input order.
 */
export function groupKitchenRows(rows, stores) {
  const famMap = new Map();   // family -> { family, order, sizeSet:Set, flavorMap:Map }
  let famSeq = 0;

  for (const row of rows || []) {
    const family = familyOf(row.category);
    if (!famMap.has(family)) famMap.set(family, { family, order: famSeq++, sizeSet: new Set(), flavorMap: new Map() });
    const fam = famMap.get(family);

    // Merge 6/8 of the same flavor by (family|variant); keep NULL-variant SKUs
    // (cks·经典组合 vs cks·特殊组合) distinct by product id so they never collapse.
    const key = row.variant ? `${family}|${row.variant}` : `id:${row.productId}`;
    if (!fam.flavorMap.has(key)) {
      fam.flavorMap.set(key, {
        key, name: displayName(row), order: fam.flavorMap.size,
        perStore: Object.fromEntries(stores.map(s => [s, { bySize: {}, nullOrders: 0, onHand: 0, total: 0 }])),
        totBySize: {}, totNullOrders: 0,
        totOrdered: 0, totOnHand: 0, totAll: 0,
      });
    }
    const fl = fam.flavorMap.get(key);
    const sz = sizeLabel(row.size);
    if (sz) fam.sizeSet.add(sz);

    for (const s of stores) {
      const ordered = row.perStore?.[s]?.ordered ?? 0;
      // A ledger that drifted negative (oversold) must never print "现 -5" on a
      // baking sheet — floor at 0, matching the worker's own guard.
      const onHand = Math.max(0, row.perStore?.[s]?.onHand ?? 0);
      const cell = fl.perStore[s];
      // Only record nonzero orders so bySize stays a clean {size:qty>0} map (the
      // column still exists via fam.sizeSet above; a store with 0 renders blank).
      if (sz) { if (ordered) { cell.bySize[sz] = (cell.bySize[sz] || 0) + ordered; fl.totBySize[sz] = (fl.totBySize[sz] || 0) + ordered; } }
      else { cell.nullOrders += ordered; fl.totNullOrders += ordered; }
      cell.onHand += onHand;
      cell.total += ordered + onHand;
      fl.totOrdered += ordered;
      fl.totOnHand += onHand;
    }
    fl.totAll = fl.totOrdered + fl.totOnHand;
  }

  return [...famMap.values()]
    .sort((a, b) => (familyRank(a.family) - familyRank(b.family)) || (a.order - b.order))
    .map(fam => ({
      family: fam.family,
      kind: kindOf(fam.family),
      sizes: [...fam.sizeSet].sort((a, b) => sizeRank(a) - sizeRank(b)),
      flavors: [...fam.flavorMap.values()].sort((a, b) => a.order - b.order),
    }));
}
